import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../api/client';
import { Icon } from '../components/Icon';
import { hapticFeedback } from '../utils/telegram';
import { formatCurrency } from '../utils/format';
import { useAppStore } from '../store/useAppStore';
import { Header } from '../components/Header';
import './NotificationsScreen.css';
import { useSwipeBack } from '../hooks/useSwipeBack';

interface UserNotification {
  id: number;
  notification_type: string;
  related_id: number;
  goal_id?: number;
  title: string;
  message?: string;
  amount?: number;
  instance_date: string;
  scheduled_time?: string;
  created_at: string;
  status: string;
  metadata?: any;
}

export const NotificationsScreen: React.FC = () => {
  const { t } = useTranslation();
  const { settings, setCurrentPage } = useAppStore();
  const containerRef = useRef<HTMLDivElement>(null);
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async (showRefresh = false) => {
    try {
      if (showRefresh) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }

      const data = await apiClient.getNotifications();
      setNotifications(data.notifications || []);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleNotificationAction = async (notification: UserNotification, action: 'confirm' | 'skip') => {
    try {
      hapticFeedback.light();

      if (action === 'confirm') {
        await apiClient.confirmNotification(notification.id);
      } else {
        // Для пропуска можно добавить API метод, но пока просто удалим уведомление
        await apiClient.skipNotification(notification.id);
      }

      // Обновляем список уведомлений
      await loadNotifications(true);

      hapticFeedback.success();
    } catch (error) {
      console.error('Failed to process notification:', error);
      hapticFeedback.error();
    }
  };

  const getNotificationIcon = (type: string, metadata?: any) => {
    switch (type) {
      case 'goal_deposit_auto':
        return <Icon name="target" size={20} />;
      case 'recurring_income':
        return <Icon name="trending-up" size={20} />;
      case 'recurring_expense':
        return <Icon name="trending-down" size={20} />;
      case 'goal_deposit':
        return <Icon name="target" size={20} />;
      case 'limit_warning':
        // Используем разные иконки в зависимости от уровня
        const level = metadata?.level;
        return <Icon name={level === 'exceeded' ? 'alert' : 'alert-circle'} size={20} />;
      default:
        return <Icon name="bell" size={20} />;
    }
  };

  const formatNotificationDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor(diffInHours * 60);
      return diffInMinutes <= 1 ? 'только что' : `${diffInMinutes} мин назад`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)} ч назад`;
    } else {
      return date.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  const renderNotification = (notification: UserNotification) => {
    const isAutoDeposit = notification.notification_type === 'goal_deposit_auto';
    const isRecurring = notification.notification_type.includes('recurring');
    const isLimitWarning = notification.notification_type === 'limit_warning';

    return (
      <div key={notification.id} className="notification-item neo-raised neo-static">
        <div className="notification-item__icon">
          {getNotificationIcon(notification.notification_type, notification.metadata)}
        </div>

        <div className="notification-item__content">
          <div className="notification-item__title">{notification.title}</div>
          {notification.message && (
            <div className="notification-item__message">{notification.message}</div>
          )}
          {notification.amount && notification.notification_type !== 'limit_warning' && (
            <div className="notification-item__amount">
              {formatCurrency(notification.amount, settings?.currency || 'RUB')}
            </div>
          )}
          <div className="notification-item__date">
            {formatNotificationDate(notification.scheduled_time || notification.created_at)}
          </div>
        </div>

        <div className="notification-item__actions">
          {isAutoDeposit ? (
            // Для автопополнения только кнопка "OK"
            <button
              className="notification-action-btn notification-action-btn--primary"
              onClick={() => handleNotificationAction(notification, 'confirm')}
            >
              OK
            </button>
          ) : isRecurring ? (
            // Для регулярных транзакций тоже только OK
            <button
              className="notification-action-btn notification-action-btn--primary"
              onClick={() => handleNotificationAction(notification, 'confirm')}
            >
              OK
            </button>
          ) : isLimitWarning ? (
            // Для уведомлений о лимитах только кнопка "OK"
            <button
              className="notification-action-btn notification-action-btn--primary"
              onClick={() => handleNotificationAction(notification, 'skip')}
            >
              OK
            </button>
          ) : (
            // Для других типов уведомлений (если будут)
            <>
              <button
                className="notification-action-btn notification-action-btn--secondary"
                onClick={() => handleNotificationAction(notification, 'skip')}
              >
                Пропустить
              </button>
              <button
                className="notification-action-btn notification-action-btn--primary"
                onClick={() => handleNotificationAction(notification, 'confirm')}
              >
                Подтвердить
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  useSwipeBack(containerRef, () => setCurrentPage('home'));

  return (
    <div ref={containerRef} className="notifications-screen page">
      <Header
        title="Уведомления"
        leftAction={
          <button
            className="neo-raised"
            onClick={() => {
              setCurrentPage('home');
              hapticFeedback.light();
            }}
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--color-primary)'
            }}
          >
            <Icon name="chevron_left" size={24} />
          </button>
        }
      />

      <div className="notifications-content">
        {isLoading ? (
          <div className="loading-state">
            <div className="loading-spinner" />
            <p>Загрузка уведомлений...</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state__icon">
              <Icon name="bell" size={64} color="#8E8E93" />
            </div>
            <div className="empty-state__title">Нет уведомлений</div>
            <div className="empty-state__subtitle">
              Здесь будут отображаться уведомления о пополнении целей и других событиях
            </div>
          </div>
        ) : (
          <>
            <div className="notifications-header">
              <button
                className="refresh-btn"
                onClick={() => loadNotifications(true)}
                disabled={isRefreshing}
              >
                <Icon name="refresh" size={16} />
                {isRefreshing ? 'Обновление...' : 'Обновить'}
              </button>
            </div>

            <div className="notifications-list">
              {notifications.map(renderNotification)}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
